import { ApiClientReturnType, KeyLabelType } from '~/types';
import apiClient from '../../api/http-common';
import { Endpoints } from '../constants';
import ENV from '~/environment';

export interface IDairyBank {
  filtered: number;
  rows: IBank[];
  total: number;
}

export interface IBank {
  id: string;
  name: string;
  accounts?: IBankAccountBad[];
  status?: string;
}

export interface IBankAccount {
  bank_id?: string;
  id?: string;
  iban: string;
  swift: string;
  depot_no: string;
  depot_name: string;
}

export interface IBankAccountBad {
  bankId: string;
  depotName: string;
  depotNo: string;
  iban: string;
  id: string;
  otc: boolean | number;
  status: boolean | number;
  swift: string;
}

export interface ILocation {
  id: string;
}

export interface ILocationOptionData {
  countries: ICountry[];
  officeTypes: IOfficeType[];
}

export interface ICountry {
  id: number;
  iso: string;
  phoneCode: string;
  label: string;
  cantons: Canton[]
}

interface IOfficeType {
  key: string;
  label: string;
}

const getDairyProfile = async () => {
  const response = await apiClient.post(`${Endpoints.DAIRY}/show`);

  return response.data;
};

// --- START - LOCATIONS API --- //

const getDairyLocations = (data?, signal?: AbortSignal) =>
  apiClient.post(`${Endpoints.LOCATIONS}/index`, data, { signal });

const getDairyLocation = (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/edit`);

const showDairyLocation = (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/show`);

const createLocation = async (data: ILocation) =>
  apiClient.post(`${Endpoints.LOCATIONS}/store`, data);

const updateLocation = async (id: string, data: ILocation) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/update`, data);

const deleteLocation = async (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/delete`);

const getLocationOptiondata = async (): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.LOCATIONS}/get-options-data`);

  return response.data;
};

// --- END - LOCATIONS API --- //

// --- START - FARMERS API --- //

const countAllFarmers = () =>
  apiClient.post('/farmers/count-all');

const countAllActiveFarmers = () =>
  apiClient.post('/farmers/count-active');

const getCountriesList = (): ApiClientReturnType<ICountry[]> =>
  apiClient.post('/countries/list');

// --- END - FARMERS API --- //

// --- START - FARMER GROUPS API --- //
const countAllFarmerGroups = () =>
  apiClient.post(`${Endpoints.FARMER_GROUPS}/count-all`);

const countAllActiveFarmerGroups = () =>
  apiClient.post(`${Endpoints.FARMER_GROUPS}/count-active`);

// --- END - FARMER GROUPS API --- //

// --- START - FARMER USERS API --- //
const countAllUsers = () =>
  apiClient.post('/users/count-all');

const countAllActiveUsers = () =>
  apiClient.post('/farmers/users/count-active');

const showUserData = (id: string) =>
  apiClient.post(`/users/${id}/show`);

// --- END - FARMER USERS API --- //

// --- START - BANKS API --- //
const getDairyBanks = async () => {
  const { data } = await apiClient.post<IDairyBank>(`${Endpoints.BANKS}/index`);
  return data;
};

const deleteDairyBankById = async (id: string): Promise<null> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/${id}/delete`);

  return data;
};

const getDairyBankById = async (id: string): Promise<IBank> => {
  const { data } = await apiClient.post<IBank>(`${Endpoints.BANKS}/${id}/edit`);

  return data;
};

const createDairyBank = async (name: any): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/store`, name);

  return data;
};

const updateDairyBank = async ({ id, bankName }): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/${id}/update`, bankName);

  return data;
};

const createBankAccount = async (data) => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/store`, data);

  return response.data;
};

const editBankAccount = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/update`, data);

  return response.data;
};

const getDairyBankAccountById = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/edit`);

  return response.data;
};

const disableDairyAccount = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/disable`);

  return response.data;
};

const enableDairyAccount = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/enable`);

  return response.data;
};
// --- END - BANKS API --- //

// --- START - DISTRICT API --- //

const getDairyStates = async () => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/index`);
  return data;
};

const deleteDairyStateById = async (id: string): Promise<null> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/${id}/delete`);

  return data;
};

const getDairyStateById = async (id: string): Promise<IBank> => {
  const { data } = await apiClient.post<IBank>(`${Endpoints.STATES}/${id}/edit`);

  return data;
};

const createDairyState = async (name: any): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/store`, name);

  return data;
};

const updateDairyState = async ({ id, bankName }): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/${id}/update`, bankName);

  return data;
};

const createStateDistrict = async (data) => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/store`, data);

  return response.data;
};

const getDistrictOptions = async () => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/get-options-data`);

  return response.data;
};

const editStateDistrict = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/${id}/update`, data);

  return response.data;
};

const getDairyStateDistrictById = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/${id}/edit`);

  return response.data;
};

const deleteStateDistrict = (id: string) =>
  apiClient.post(`${Endpoints.DISTRICT}/${id}/delete`)

// --- END - DISTRICT API --- //

// --- START - MILK TYPES API --- //

const getMilkTypes = () =>
  apiClient.post(`${Endpoints.MILK_TYPES}/index`)

const createMilkType = (data: any) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/store`, data)

const updateMilkType = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.MILK_TYPES}/${id}/update`, data)

  return response.data;
};

const editMilkType = (id: string) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/${id}/edit`)

const deleteMilkType = (id: string) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/${id}/delete`)

// --- END - MILK TYPES API --- //

// --- START - HUSBANDRY API --- //

const getHusbandryForms = (data?) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/index`, data);

const editHusbandryForm = (id: string) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/edit`);

const createHusbandryForm = async (data) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/store`, data);

const updateHusbandryForm = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/update`, data)

  return response.data;
};

const deleteHusbandryForm = async (id: string) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/delete`);

// --- END - HUSBANDRY API --- //

const getDairyLogo = async (data: string) => {
  const response = await apiClient.post('/dairies/get-file', { fileName: data });
  return response.data;
};

// Associations

const getAssociations = () => apiClient.post('/associations/index')

const getAssociationsList = () => apiClient.post('/associations/index')

const storeAssociation = (data) => apiClient.post('/associations/store', data)

const updateAssociation = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/associations/${id}/update`, data)

  return response.data;
};

const editAssociation = (id) => apiClient.post(`/associations/${id}/edit`)

const deleteAssociation = (id) => apiClient.post(`/associations/${id}/delete`)

// Membership statuses

const getMembershipStatuses = () => apiClient.post('/membership-statuses/index')

const getMembershipStatusesList = () => apiClient.post('/membership-statuses/list')

const storeMembershipStatus = (data) => apiClient.post('/membership-statuses/store', data)

const updateMembershipStatus = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/membership-statuses/${id}/update`, data)

  return response.data;
};

const editMembershipStatus = (id) => apiClient.post(`/membership-statuses/${id}/edit`)

const deleteMembershipStatus = (id) => apiClient.post(`/membership-statuses/${id}/delete`)

// Managers

const getManagers = (data?) =>
  apiClient.post('/managers/index', data)

const getManagersOptions = (): ApiClientReturnType<ManagersOptionsType> =>
  apiClient.post('/managers/get-options-data')

const getManagersIndexOptions = (): ApiClientReturnType<ManagersIndexOptionsType> =>
  apiClient.post('/managers/get-index-data')

const getManagersList = (): ApiClientReturnType<{ id: string, name: string }[]> =>
  apiClient.post('/managers/list')

const storeManager = (data) =>
  apiClient.post('/managers/store', data)

const countManagers = (data) =>
  apiClient.post('/managers/count', data)

const showManagerData = (id: string): ApiClientReturnType<ManagerShowType> =>
  apiClient.post(`/managers/${id}/show`)

const editManager = (id: string) =>
  apiClient.post(`/managers/${id}/edit`)

const updateManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/update`, data)

const getManagerUsers = (data) =>
  apiClient.post('/managers/users/index', data)

const getManagerOptions = (data): ApiClientReturnType<{ types: KeyLabelType[], languages: { id: string, label: string }[] }> =>
  apiClient.post('/managers/users/get-index-data', data)

const countActiveManagerUsers = (data) =>
  apiClient.post('/managers/users/count-active', data)

const getCountManagerUsers = (data) =>
  apiClient.post('/managers/users/count', data)

const getFarmersOfManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/farmers/index`, data)

const getCollectionsOfManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/collection-points/index`, data)

const getFinancesManager = (id: string) =>
  apiClient.post(`/managers/${id}/finances/bank-data/get-data`)

const checkFinancesManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/finances/bank-data/check-data`, data)

const updateFinancesManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/finances/bank-data/update`, data)

const getTaxDataManager = (id: string) =>
  apiClient.post(`/managers/${id}/finances/tax-data/get-data`)

const updateTaxDataManager = (id: string, data) =>
  apiClient.post(`/managers/${id}/finances/bank-data/update`, data)

const getManagerMilkPaymentsData = (id: string): ApiClientReturnType<InvoiceOptionsType> =>
  apiClient.post(`/managers/${id}/temp/milk-payments/get-index-data`)

const getManagerMilkPayments = (id: string, data?: any) =>
  apiClient.post(`/managers/${id}/temp/milk-payments/index`, data)

// Certifications

const getCertifications = () => apiClient.post('/certifications/index')

// Collection Cycles

const getCollectionCycles = () => apiClient.post('/collection-cycles/index')

const getCollectionCyclesList = () => apiClient.post('/collection-cycles/list')

const storeCollectionCycle = (data) => apiClient.post('/collection-cycles/store', data)

const updateCollectionCycle = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/collection-cycles/${id}/update`, data)

  return response.data;
};

const editCollectionCycle = (id) => apiClient.post(`/collection-cycles/${id}/edit`)

const deleteCollectionCycle = (id) => apiClient.post(`/collection-cycles/${id}/delete`)

// Contract Types

const getContractTypesList = () => apiClient.post('/contract-types/index')

// Collection Methods

const getCollectionMethodsList = () => apiClient.post('/collection-methods/index')

// Billing Groups

const getBillingGroupsList = () => apiClient.post('/billing-groups/index')

// Transport Tariffs

const getTransportTariffsList = () => apiClient.post('/transport-tariffs/index')

// Transportation Areas

const getTransportationAreas = () => apiClient.post('/transport-areas/index')

const getTransportationAreasList = () => apiClient.post('/transport-areas/list')

const storeTransportationArea = (data) => apiClient.post('/transport-areas/store', data)

const updateTransportationArea = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/transport-areas/${id}/update`, data)

  return response.data;
};

const editTransportationArea = (id) => apiClient.post(`/transport-areas/${id}/edit`)

const deleteTransportationArea = (id) => apiClient.post(`/transport-areas/${id}/delete`)

// Production Zones

const getProductionZones = () => apiClient.post('/production-zones/index')

const getProductionZonesList = () => apiClient.post('/production-zones/list')

const storeProductionZone = (data) => apiClient.post('/production-zones/store', data)

const updateProductionZone = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/production-zones/${id}/update`, data)

  return response.data;
};

const editProductionZone = (id) => apiClient.post(`/production-zones/${id}/edit`)

const deleteProductionZone = (id) => apiClient.post(`/production-zones/${id}/delete`)

// Operating Forms

const getOperatingForms = () => apiClient.post('/operating-forms/index')

const getOperatingFormsList = () => apiClient.post('/operating-forms/list')

const storeOperatingForm = (data) => apiClient.post('/operating-forms/store', data)

const updateOperatingForm = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/operating-forms/${id}/update`, data)

  return response.data;
};

const editOperatingForm = (id) => apiClient.post(`/operating-forms/${id}/edit`)

const deleteOperatingForm = (id) => apiClient.post(`/operating-forms/${id}/delete`)

// Transponder IDs

const getTransponderIds = (farmerId: string) => apiClient.post(`/farmers/${farmerId}/transponders/index`)

const storeTransponderId = (farmerId: string, data) => apiClient.post(`/farmers/${farmerId}/transponders/store`, data)

const updateTransponderId = async (farmerId: string, id: string, data): Promise<any> => {
  const response = await apiClient.post(`/farmers/${farmerId}/transponders/${id}/update`, data)

  return response.data;
};

const editTransponderId = (farmerId: string, id: string) => apiClient.post(`/farmers/${farmerId}/transponders/${id}/edit`)

const deleteTransponderId = (farmerId: string, id: string) => apiClient.post(`/farmers/${farmerId}/transponders/${id}/delete`)

// Collection Points

const getCollectionPoints = (data?) => apiClient.post('/collection-points/index', data)

const getCollectionPointsOptions = (): ApiClientReturnType<CollectionPointsOptionsType> => apiClient.post('/collection-points/get-options-data')

const storeCollectionPoint = (data) => apiClient.post('/collection-points/store', data)

const showCollectionPoint = (id: string): ApiClientReturnType<CollectionPointShowType> => apiClient.post(`/collection-points/${id}/show`)

const editCollectionPoint = (id: string) => apiClient.post(`/collection-points/${id}/edit`)

const updateCollectionPoint = (id: string, data) => apiClient.post(`/collection-points/${id}/update`, data)

const deleteCollectionPoint = (id: string) => apiClient.post(`/collection-points/${id}/delete`)

// Collection Sites

const getCollectionSites = (data?) => apiClient.post('/collection-sites/index', data)

const storeCollectionSite = (data) => apiClient.post('/collection-sites/store', data)

const editCollectionSite = (id: string) => apiClient.post(`/collection-sites/${id}/edit`)

const updateCollectionSite = (id: string, data) => apiClient.post(`/collection-sites/${id}/update`, data)

const deleteCollectionSite = (id: string) => apiClient.post(`/collection-sites/${id}/delete`)

// Segmentation Prices

const getSegmentationPricesOptions = (data?): ApiClientReturnType<PricesSegmType> =>
  apiClient.post('/segmentation/prices/get-options-data', data)

const storeSegmentationPrice = (data?) =>
  apiClient.post('/segmentation/prices/store', data)

const editSegmentationPrice = (id: string) =>
  apiClient.post(`/segmentation/prices/${id}/edit`)

const updateSegmentationPrice = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/segmentation/prices/${id}/update`, data)
  return response.data
}

const getSegmentationPricesIndexData = (data?) =>
  apiClient.post('/segmentation/prices/get-index-data', data)

const getSegmentationPrices = (data?) =>
  apiClient.post('/segmentation/prices/index', data)

// Segmentation Files

const getSegmentationFilesOptions = (data?): ApiClientReturnType<{ months: KeyLabelType[] }> =>
  apiClient.post('/segmentation/files/get-options-data', data)

const storeSegmentationFile = (data?) =>
  apiClient.post('/segmentation/files/store', data)

const getSegmentationFilesIndexData = (data?) =>
  apiClient.post('/segmentation/files/get-index-data', data)

const getSegmentationFiles = (data?) =>
  apiClient.post('/segmentation/files/index', data)

const getSegmentationFile = (id: string) =>
  apiClient.post(`/segmentation/files/${id}/get-file`)

const getSegmentationFileByLang = (id: string, fileName: string) =>
  apiClient.post(`/segmentation/files/${id}/get-file`, { fileName })

const editSegmentationFile = (id: string) =>
  apiClient.post(`/segmentation/files/${id}/edit`)

const deleteSegmentationFile = (id: string) =>
  apiClient.post(`/segmentation/files/${id}/delete`)

// Restriction Periods

const getRestrictionPeriods = () => apiClient.post('/restriction-periods/index')

const getRestrictionPeriodsList = () => apiClient.post('/restriction-periods/list')

const storeRestrictionPeriod = (data) => apiClient.post('/restriction-periods/store', data)

const updateRestrictionPeriod = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`/restriction-periods/${id}/update`, data)

  return response.data;
};

const editRestrictionPeriod = (id) => apiClient.post(`/restriction-periods/${id}/edit`)

const deleteRestrictionPeriod = (id) => apiClient.post(`/restriction-periods/${id}/delete`)

const DairyService = {
  getDairyProfile,
  getDairyLogo,
  getDairyBanks,
  createDairyBank,
  updateDairyBank,
  getDairyBankById,
  deleteDairyBankById,
  createBankAccount,
  editBankAccount,
  createLocation,
  getLocationOptiondata,
  getDairyLocations,
  getDairyLocation,
  updateLocation,
  deleteLocation,
  getDairyBankAccountById,
  disableDairyAccount,
  enableDairyAccount,
  countAllFarmerGroups,
  countAllFarmers,
  countAllUsers,
  showDairyLocation,
  showUserData,
  countAllActiveFarmerGroups,
  countAllActiveFarmers,
  countAllActiveUsers,
  getDairyStateById,
  getDairyStates,
  getDairyStateDistrictById,
  updateDairyState,
  deleteDairyStateById,
  createStateDistrict,
  createDairyState,
  editStateDistrict,
  getDistrictOptions,
  deleteStateDistrict,
  getMilkTypes,
  createMilkType,
  updateMilkType,
  editMilkType,
  deleteMilkType,
  getHusbandryForms,
  editHusbandryForm,
  deleteHusbandryForm,
  updateHusbandryForm,
  createHusbandryForm,
  getAssociations,
  getAssociationsList,
  storeAssociation,
  updateAssociation,
  editAssociation,
  deleteAssociation,
  getMembershipStatuses,
  getMembershipStatusesList,
  storeMembershipStatus,
  updateMembershipStatus,
  editMembershipStatus,
  deleteMembershipStatus,
  getManagers,
  getManagersOptions,
  getManagersList,
  storeManager,
  showManagerData,
  updateManager,
  editManager,
  getCertifications,
  getCollectionCycles,
  getCollectionCyclesList,
  storeCollectionCycle,
  updateCollectionCycle,
  editCollectionCycle,
  deleteCollectionCycle,
  getContractTypesList,
  getCollectionMethodsList,
  getBillingGroupsList,
  getTransportTariffsList,
  getTransportationAreas,
  getTransportationAreasList,
  storeTransportationArea,
  updateTransportationArea,
  editTransportationArea,
  deleteTransportationArea,
  getProductionZones,
  getProductionZonesList,
  storeProductionZone,
  updateProductionZone,
  editProductionZone,
  deleteProductionZone,
  getOperatingForms,
  getOperatingFormsList,
  storeOperatingForm,
  updateOperatingForm,
  editOperatingForm,
  deleteOperatingForm,
  getTransponderIds,
  storeTransponderId,
  updateTransponderId,
  editTransponderId,
  deleteTransponderId,
  getCollectionPoints,
  getCollectionPointsOptions,
  storeCollectionPoint,
  showCollectionPoint,
  editCollectionPoint,
  updateCollectionPoint,
  deleteCollectionPoint,
  getCollectionSites,
  storeCollectionSite,
  editCollectionSite,
  updateCollectionSite,
  deleteCollectionSite,
  getSegmentationPricesOptions,
  storeSegmentationPrice,
  editSegmentationPrice,
  updateSegmentationPrice,
  getSegmentationPricesIndexData,
  getSegmentationPrices,
  getSegmentationFilesOptions,
  storeSegmentationFile,
  getSegmentationFilesIndexData,
  getSegmentationFiles,
  getSegmentationFile,
  getSegmentationFileByLang,
  editSegmentationFile,
  deleteSegmentationFile,
  getRestrictionPeriods,
  getRestrictionPeriodsList,
  storeRestrictionPeriod,
  updateRestrictionPeriod,
  editRestrictionPeriod,
  deleteRestrictionPeriod,
  getFarmersOfManager,
  getCollectionsOfManager,
  getFinancesManager,
  checkFinancesManager,
  updateFinancesManager,
  getTaxDataManager,
  updateTaxDataManager,
  getCountriesList,
  getManagerOptions,
  getManagerUsers,
  countActiveManagerUsers,
  getCountManagerUsers,
  getManagersIndexOptions,
  countManagers,
  getManagerMilkPaymentsData,
  getManagerMilkPayments
};

export interface ManagersOptionsType {
  associations: Association[];
  membershipStatuses: MembershipStatus[];
  countries: Country[];
  languages: Language[];
}

export interface Country {
  id: number;
  iso: string;
  phoneCode: string;
  label: string;
  cantons: Canton[];
}

export interface Canton {
  id: number;
  countryId: number;
  slug: string;
  name: string;
  label: string;
  abbreviation: string;
}

export interface Language {
  id: number;
  code: string;
  order: number;
  icon: string;
  label: string;
}

export interface MembershipStatus {
  id: number;
  status: number;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  label: string;
  membershipStatusId: number;
  languageId: number;
}

export interface ManagerShowType {
  id: string;
  agisId: string;
  membershipStatusId: number;
  name: string;
  burNumber: string;
  languageId: number;
  phonePrefix: number;
  phoneNumber: string;
  digitalPayment: number;
  membershipApplicationDate: null;
  membershipApplicationFile: null;
  cancellationDate: null;
  createdAt: Date;
  updatedAt: Date;
  associations: Association[];
  status: number;
  membershipStatusName: string;
  label: string;
}

export interface Association {
  id: number;
  status: number;
  managerId: string;
  associationId: number;
  name: string;
  abbreviation: string;
  label: string;
  languageId: number;
}

export interface CollectionPointsOptionsType {
  languages: Language[];
  associations: Association[];
  collectionMethods: CollectionMethod[];
  countries: Country[];
  contractTypes: CollectionMethod[];
  billingGroups: Association[];
  tranportTariffs: Association[];
  milkTypes: MilkType[];
  milkTypeCharacteristics: CollectionMethod[];
}

export interface CollectionMethod {
  id: number;
  slug: string;
  label: string;
  status?: number;
  order?: number;
}

export interface MilkType {
  id: number;
  default: number;
  abbreviation: string;
  name: string;
  label: string;
  milkTypeId: number;
  languageId: number;
}

export interface CollectionPointShowType {
  id: string;
  uniqueId: string;
  name: string;
  businessUnit: number;
  managers: Manager[];
  createdAt: Date;
  updatedAt: Date;
}

export interface Manager {
  id: string;
  name: string;
}

export interface ManagersIndexOptionsType {
  types: Type[];
  membershipStatuses: MembershipStatus[];
}
export interface Type {
  key: string;
  label: string;
}

export interface PricesSegmType {
  months: KeyLabelType[];
  milkTypes: MilkSegmentationType[];
}

export interface MilkSegmentationType {
  id: number;
  abbreviation: string;
  name: string;
  label: string;
  milkTypeId: number;
  languageId: number;
}

export interface InvoiceOptionsType {
  farmers: Farmer[];
}

export interface Farmer {
  id: string;
  companyName: string;
  evsNumber: string;
  managerId: string;
  farmerId: string;
}

export default DairyService;
